import React, { useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Form } from '@unform/web';
import { FormHandles, SubmitHandler } from '@unform/core';
import * as Yup from 'yup';

import { GridUserAlerts } from 'components/settings/Alerts';
import { Select } from 'components/shared/Form';
import { useTranslation, useValidation } from 'hooks';

import {
  ListUserAlertsActions,
  ListUserAlertsState,
  CreateUserAlertActions,
  CreateUserAlertState,
} from 'store/ducks/settings/user-alerts';
import { ListUsersActions, ListUsersState } from 'store/ducks/settings/users';
import {
  ListBusinessActions,
  ListBusinessState,
} from 'store/ducks/settings/business';
import {
  ListCompaniesActions,
  ListCompaniesState,
} from 'store/ducks/settings/companies';

import { translations } from './translations';
import * as S from './styles';

interface IParams {
  id: string;
}

const UserAlerts: React.FC = () => {
  const { id } = useParams<IParams>();
  const dispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);

  const { getTranslation } = useTranslation(translations);
  const { handleFormErrors } = useValidation();
  const idsAlertAcceptFields = ['7', '8', '32'];

  /* ===== * Selectors * ===== */
  const { data: users, loading: usersLoading } = useSelector<
    any,
    ListUsersState
  >((state) => state.listUsers);

  const { data: userAlerts, loading: listLoading } = useSelector<
    any,
    ListUserAlertsState
  >((state) => state.listUserAlerts);

  const { data: business, loading: businessLoading } = useSelector<
    any,
    ListBusinessState
  >((state) => state.listBusiness);

  const { data: companies, loading: companiesLoading } = useSelector<
    any,
    ListCompaniesState
  >((state) => state.listCompanies);

  const { loading: createLoading } = useSelector<any, CreateUserAlertState>(
    (state) => state.createUserAlert
  );

  /* ===== * Callbacks * ===== */
  const fetchUserAlertsList = useCallback(() => {
    dispatch(ListUserAlertsActions.request({ alert_id: id }));
  }, [dispatch, id]);

  const onSuccessCreate = useCallback(() => {
    formRef.current?.setData({});
    formRef.current?.reset();

    fetchUserAlertsList();
  }, [fetchUserAlertsList]);

  const handleSubmit = useCallback<SubmitHandler>(
    async (values) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          user_id: Yup.number().required(getTranslation('obrigatorio')),
        });

        await schema.validate(values, { abortEarly: false });

        dispatch(
          CreateUserAlertActions.request(
            {
              general_alert_id: id,
              general_user_id: values.user_id,
              general_company_ids: values.general_company_ids,
              business_line_ids: values.business_line_ids,
            },
            onSuccessCreate
          )
        );
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, getTranslation, handleFormErrors, id, onSuccessCreate]
  );

  /* ===== * Effects * ===== */
  useEffect(() => {
    dispatch(ListUsersActions.request({ all: true }));
    dispatch(ListCompaniesActions.request({ all: true, type: 'CD' }));
    dispatch(ListBusinessActions.request({ all: true }));
    fetchUserAlertsList();
  }, [dispatch, fetchUserAlertsList]);

  /* ===== * Render * ===== */
  return (
    <S.PageContent>
      <Form ref={formRef} onSubmit={handleSubmit} placeholder="">
        <S.BoxContainer>
          <S.FormRow>
            <Select
              name="user_id"
              label={getTranslation('usuarios')}
              options={users}
              isLoading={usersLoading}
              isDisabled={usersLoading}
              placeholder={getTranslation('selecione')}
            />

            {idsAlertAcceptFields.includes(id) && (
              <>
                {' '}
                <Select
                  name="general_company_ids"
                  label={getTranslation('centro')}
                  options={companies}
                  isLoading={companiesLoading}
                  isDisabled={companiesLoading}
                  placeholder={getTranslation('selecione')}
                  isMulti
                />
                <Select
                  name="business_line_ids"
                  label={getTranslation('businessLine')}
                  options={business}
                  isLoading={businessLoading}
                  isDisabled={businessLoading}
                  placeholder={getTranslation('selecione')}
                  isMulti
                />
              </>
            )}
          </S.FormRow>
        </S.BoxContainer>

        <S.FormFooter>
          <S.Button type="submit" disabled={createLoading}>
            {createLoading ? <S.Loading /> : getTranslation('adicionar')}
          </S.Button>
        </S.FormFooter>
      </Form>

      <GridUserAlerts
        userAlerts={userAlerts}
        loading={createLoading || listLoading}
        onDelete={fetchUserAlertsList}
        extraColumns={idsAlertAcceptFields.includes(id)}
      />
    </S.PageContent>
  );
};

export default UserAlerts;
